import { ArrowLeft, AlertCircle } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useI18n } from '@/contexts/I18nContext'
import { useNotificationPreferences } from '@/shared/hooks/useNotificationPreferences'
import { Switch } from '@/components/ui/switch'
import { Skeleton } from '@/components/ui/skeleton'
import { TopNavigation } from '@/components/layout/TopNavigation'

const SettingsNotifications = () => {
  const navigate = useNavigate()
  const { t } = useI18n()
  const { preferences, isLoading, error, updatePreference } = useNotificationPreferences()

  const isNotificationBlocked = typeof Notification !== 'undefined' && Notification.permission === 'denied'

  const handleToggle = async (key: 'training_reminders' | 'class_requests' | 'direct_messages' | 'roll_review') => {
    await updatePreference(key, !preferences[key])
  }

  return (
    <div className="min-h-screen bg-[#160C0C] text-white">
      <TopNavigation />

      <div className="pt-16">
        {/* Header */}
        <div className="relative flex items-center px-4 py-3.5 border-b border-[#281B1B]">
          <button onClick={() => navigate(-1)} className="p-1 text-white/80 hover:text-white transition-colors z-10">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="absolute left-1/2 -translate-x-1/2 text-base font-semibold truncate max-w-[60%]">
            {t('settings.notification_prefs.title')}
          </h1>
        </div>

      {/* Content */}
      <div className="px-4 pt-6 space-y-6">
        {/* Section label */}
        <div className="text-xs font-semibold text-white/40 uppercase tracking-wide">
          {t('settings.notification_prefs.section_push')}
        </div>

        {/* Loading state */}
        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="flex items-center justify-between py-3">
                <div className="space-y-1 flex-1">
                  <Skeleton className="h-4 w-32 bg-white/10" />
                  <Skeleton className="h-3 w-48 bg-white/5" />
                </div>
                <Skeleton className="h-6 w-10 bg-white/10" />
              </div>
            ))}
          </div>
        ) : (
          <>
            {/* Preference rows */}
            <PreferenceRow
              title={t('settings.notification_prefs.training_reminders_title')}
              subtitle={t('settings.notification_prefs.training_reminders_subtitle')}
              enabled={preferences.training_reminders}
              onChange={() => handleToggle('training_reminders')}
              disabled={isLoading}
            />

            <PreferenceRow
              title={t('settings.notification_prefs.class_requests_title')}
              subtitle={t('settings.notification_prefs.class_requests_subtitle')}
              enabled={preferences.class_requests}
              onChange={() => handleToggle('class_requests')}
              disabled={isLoading}
            />

            <PreferenceRow
              title={t('settings.notification_prefs.direct_messages_title')}
              subtitle={t('settings.notification_prefs.direct_messages_subtitle')}
              enabled={preferences.direct_messages}
              onChange={() => handleToggle('direct_messages')}
              disabled={isLoading}
            />

            <PreferenceRow
              title={t('settings.notification_prefs.roll_review_title')}
              subtitle={t('settings.notification_prefs.roll_review_subtitle')}
              enabled={preferences.roll_review}
              onChange={() => handleToggle('roll_review')}
              disabled={isLoading}
            />
          </>
        )}

        {/* Error state (non-blocking) */}
        {error && (
          <div className="mt-4 p-3 rounded-lg bg-red-950/20 border border-red-800/30 flex gap-3">
            <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-red-300">{t('settings.notification_prefs.load_error')}</p>
          </div>
        )}

        {/* OS permission note */}
        <div className="mt-8 pt-6 border-t border-white/10">
          <div className="flex gap-2">
            <AlertCircle className="w-4 h-4 text-white/40 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-white/60">{t('settings.notification_prefs.permission_note')}</p>
          </div>
        </div>

        {/* OS permission blocked warning */}
        {isNotificationBlocked && (
          <div className="p-3 rounded-lg bg-orange-950/20 border border-orange-800/30 flex gap-3">
            <AlertCircle className="w-4 h-4 text-orange-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-orange-300">{t('settings.notification_prefs.permission_blocked')}</p>
          </div>
        )}
      </div>
      </div>
    </div>
  )
}

interface PreferenceRowProps {
  title: string
  subtitle: string
  enabled: boolean
  onChange: () => void
  disabled?: boolean
}

const PreferenceRow = ({ title, subtitle, enabled, onChange, disabled }: PreferenceRowProps) => (
  <div className="flex items-center justify-between py-3 px-0">
    <div className="flex-1 min-w-0">
      <h3 className="text-sm font-medium text-white leading-tight">{title}</h3>
      <p className="text-xs text-white/60 mt-1">{subtitle}</p>
    </div>
    <Switch
      checked={enabled}
      onCheckedChange={onChange}
      disabled={disabled}
      className="ml-4 flex-shrink-0"
    />
  </div>
)

export default SettingsNotifications
